import React, { useState, useEffect } from "react";
import moment from "moment";
import { Link } from "react-router-dom";
import PatientAutocomplete from "../components/PatientAutocomplete";
import CustomSelect from "../components/CustomSelect";
import {
  createAppointmentWithoutOTP,
  serviceCreateAppointment,
  serviceHandleAddPatientSubmit,
  serviceFetchDoctorList,
} from "../services/api";
import { getClinicIdFromToken, getValueFromToken } from "../utils/tokenUtils";
import { addPatientValidation } from "../utils/validations/addPatientValidation";
import * as Yup from "yup";
import {
  calculateAgeR,
  calculateDateOfBirthFromAgeR,
} from "../utils/commonUtils";
import { showToast } from "../utils/toastUtils";
import { FaSpinner, FaDollarSign } from "react-icons/fa";
import dayjs from "dayjs";
import AntDatePicker from "../components/antDatepicker";
import BillingForm from "./BillingForm";
import { Tooltip as ReactTooltip } from "react-tooltip";

const RapidEntry = () => {
  // Form state
  const [patient, setPatient] = useState("");
  const [patientDetails, setPatientDetails] = useState({});
  const [checkRelatedPatient, setCheckRelatedPatient] = useState(false);
  const [localSelectedDoctor, setLocalSelectedDoctor] = useState("");
  const [errorMessage, setErrorMessage] = useState({});
  const [name, setName] = useState("");
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("");
  const [isNewRelatedPatient, setIsNewRelatedPatient] = useState(false);
  const [date_of_birth, setBirthDate] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Rapid entry specific state
  const [appointmentDate, setAppointmentDate] = useState(dayjs());
  const [appointmentStatus, setAppointmentStatus] = useState(2); // 2 = Completed
  const [doctors, setDoctors] = useState([]);
  const [addedAppointments, setAddedAppointments] = useState([]);

  // Billing modal state
  const [showBillingForm, setShowBillingForm] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [selectedAppointmentDate, setSelectedAppointmentDate] = useState(null);
  const [selectedAppointmentDoctor, setSelectedAppointmentDoctor] = useState(null);

  // Fetch doctors on mount
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        const userId = getValueFromToken("user_id");
        const response = await serviceFetchDoctorList(userId, true);
        if (response?.status && response?.data?.doctors) {
          setDoctors(response.data.doctors);
        }
      } catch (error) {
        console.error("Error fetching doctors:", error);
      }
    };
    fetchDoctors();
  }, []);

  const createAppointment = async (patientId, doctorId) => {
    let params = {
      patientId,
      doctorId,
      clinicId: getClinicIdFromToken(),
      appointmentDate: appointmentDate.format("YYYY-MM-DD"),
      status: appointmentStatus,
    };
    let response = await serviceCreateAppointment(params);
    if (response.status) {
      return { success: true, appointmentId: response.data?.appointment_id };
    } else {
      return { success: false, message: response.message };
    }
  };

  const handleAddPatientSubmit = async (patientData) => {
    const {
      name,
      age,
      gender,
      patientContactNumber,
      localSelectedDoctor,
      isNewRelatedPatient,
      patientDetails,
      date_of_birth,
    } = patientData;

    try {
      setIsSubmitting(true);
      await addPatientValidation.validate(
        {
          name,
          age,
          gender,
          patientContactNumber,
          localSelectedDoctor,
          date_of_birth,
        },
        { abortEarly: false }
      );

      let appointmentResult = null;
      let createdPatientId = null;

      if (checkRelatedPatient) {
        let params = {
          name: name,
          age: age,
          gender: gender,
          date_of_birth: date_of_birth,
          patientDetails,
        };
        const response = await serviceHandleAddPatientSubmit(params);
        if (response?.status) {
          if (response?.data?.added) {
            const newPatientId = response?.data?.patientId;
            createdPatientId = newPatientId;
            appointmentResult = await createAppointment(newPatientId, localSelectedDoctor);
          }
        }
      } else if (patientDetails && patientDetails?.patient_id) {
        createdPatientId = patientDetails.patient_id;
        appointmentResult = await createAppointment(patientDetails.patient_id, localSelectedDoctor);
        if (!appointmentResult?.success) {
          showToast(appointmentResult.message, "error");
          setIsSubmitting(false);
          return;
        }
      } else {
        const patientDataToSubmit = {
          patient_name: name,
          patient_age: age,
          gender: gender,
          patient_contact_number: patientContactNumber,
          date_of_birth: date_of_birth,
        };
        let response = await createAppointmentWithoutOTP(
          patientDataToSubmit,
          localSelectedDoctor,
          getClinicIdFromToken(),
          appointmentDate.format("YYYY-MM-DD"),
          appointmentStatus
        );
        if (response?.status) {
          createdPatientId = response?.data?.patientId;
          appointmentResult = {
            success: true,
            appointmentId: response?.data?.appointmentId,
          };
          showToast(response.message, "success");
        } else {
          console.error("Failed to create appointment for new patient");
          showToast(response.message, "error");
          setIsSubmitting(false);
          return;
        }
      }

      // Add to the appointments list
      if (appointmentResult?.success) {
        const selectedDoctor = doctors.find((d) => d.doctor_id === localSelectedDoctor);
        const newAppointment = {
          appointmentId: appointmentResult.appointmentId,
          patientId: createdPatientId,
          patientName: name,
          doctorName: selectedDoctor
            ? `${selectedDoctor.first_name} ${selectedDoctor.last_name}`
            : "",
          doctorId: localSelectedDoctor,
          status: appointmentStatus,
          statusLabel:
            appointmentStatus === 0
              ? "Waiting"
              : appointmentStatus === 1
              ? "In Progress"
              : appointmentStatus === 2
              ? "Completed"
              : "Canceled",
          time: moment().format("hh:mm A"),
          date: appointmentDate.format("YYYY-MM-DD"),
        };
        setAddedAppointments([newAppointment, ...addedAppointments]);
        showToast("Appointment added successfully", "success");
      }

      // Reset only patient-specific fields, keep date/status/doctor
      setName("");
      setAge("");
      setGender("");
      setBirthDate("");
      setPatient("");
      setPatientDetails({});
      setErrorMessage({});
      setIsNewRelatedPatient(false);
      setCheckRelatedPatient(false);

      // Focus back on phone number input
      setTimeout(() => {
        const phoneInput = document.getElementById("mobile");
        if (phoneInput) phoneInput.focus();
      }, 100);
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const validationErrors = {};
        error.inner.forEach((err) => {
          validationErrors[err.path] = err.message;
        });
        setErrorMessage(validationErrors);
      } else {
        console.error("Network error:", error);
        setErrorMessage({ customError: "Network error. Please try again." });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLocalSubmit = async (e) => {
    e.preventDefault();
    await handleAddPatientSubmit({
      name,
      age,
      gender,
      patientContactNumber: patient,
      localSelectedDoctor,
      isNewRelatedPatient,
      patientDetails,
      date_of_birth,
    });
  };

  const resetForm = () => {
    setName("");
    setAge("");
    setGender("");
    setPatient("");
    setPatientDetails({});
    setIsNewRelatedPatient(false);
    setCheckRelatedPatient(false);
    setBirthDate("");
  };

  const CalculateAgeFromDate = (date) => {
    if (date) {
      setBirthDate(dayjs(date).startOf("day").format("YYYY-MM-DD"));
      let localAge = calculateAgeR(date);
      setAge(localAge);
    }
  };

  const handleChangeAge = (event) => {
    if (isNaN(event.target.value)) {
      setAge("");
      showToast("Only number allowed", "error");
    } else {
      if (event.target.value < 1) {
        let localDob = calculateDateOfBirthFromAgeR(age);
        setBirthDate(dayjs(localDob).format("YYYY-MM-DD"));
        CalculateAgeFromDate(dayjs(localDob).format("YYYY-MM-DD"));
      }
      let localDob = calculateDateOfBirthFromAgeR(age);
      setBirthDate(dayjs(localDob).format("YYYY-MM-DD"));
    }
  };

  const handleOpenBilling = (appointment) => {
    setSelectedPatientId(appointment.patientId);
    setSelectedAppointmentDate(appointment.date);
    setSelectedAppointmentDoctor({
      doctor_id: appointment.doctorId,
      full_name: appointment.doctorName,
    });
    setShowBillingForm(true);
  };

  const handleCloseBillingForm = () => {
    setSelectedPatientId(null);
    setShowBillingForm(false);
  };

  return (
    <div className="page-container relative h-full flex flex-auto flex-col px-4 sm:px-6 py-4 sm:py-6 md:px-8">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-xl font-semibold text-gray-800">
          Rapid Entry - Add Appointments
        </h1>
        <Link
          to={`/clinic/dashboard?date=${appointmentDate.format("YYYY-MM-DD")}`}
          className="px-4 py-2 bg-blue-500 text-white text-sm rounded-md hover:bg-blue-600 transition-colors"
        >
          Dashboard
        </Link>
      </div>

      <div className={`flex gap-6 h-full ${showBillingForm ? "blur-sm pointer-events-none" : ""}`}>
        {/* Left Panel - Added Appointments */}
        <div className="w-[60%] bg-white border border-gray-200 rounded-lg p-4 overflow-y-auto">
          {/* <h2 className="text-lg font-semibold text-gray-700 mb-3">
            Added Appointments ({addedAppointments.length})
          </h2> */}
          {addedAppointments.length === 0 ? (
            <p className="text-gray-500 text-sm">
              No appointments added yet.
            </p>
          ) : (
            <div className="overflow-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left text-xs font-medium text-gray-600 py-2 px-3">Patient</th>
                    <th className="text-left text-xs font-medium text-gray-600 py-2 px-3">Doctor</th>
                    <th className="text-left text-xs font-medium text-gray-600 py-2 px-3">Date</th>
                    <th className="text-left text-xs font-medium text-gray-600 py-2 px-3">Status</th>
                    <th className="text-right text-xs font-medium text-gray-600 py-2 px-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {addedAppointments.map((appt, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-3 text-sm text-gray-800">{appt.patientName}</td>
                      <td className="py-3 px-3 text-sm text-gray-700">Dr. {appt.doctorName}</td>
                      <td className="py-3 px-3 text-sm text-gray-600">{dayjs(appt.date).format("DD MMM YYYY")}</td>
                      <td className="py-3 px-3">
                        <span
                          className={`text-xs font-medium px-2.5 py-0.5 rounded ${
                            appt.status === 0
                              ? "bg-brown-100 text-brown-800"
                              : appt.status === 1
                              ? "bg-yellow-100 text-yellow-800"
                              : appt.status === 2
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {appt.statusLabel}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-right">
                        <div
                          onClick={() => handleOpenBilling(appt)}
                          data-tooltip-id={`bill-${index}`}
                          className="inline-grid grid-cols-[1fr] p-3 py-1.5 gap-x-2 font-medium tracking-wide text-center text-white capitalize transition-colors duration-300 transform bg-blue-500 rounded-md hover:bg-blue-600 focus:outline-none focus:ring focus:ring-teal-300 focus:ring-opacity-80 cursor-pointer"
                        >
                          <div className="flex items-center justify-center">
                            <FaDollarSign className="w-4 h-4" />
                          </div>
                        </div>
                        <ReactTooltip
                          id={`bill-${index}`}
                          place="bottom"
                          content="Create Bill"
                          className="!text-xs !px-3 !py-1"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Right Panel - Appointment Form */}
        <div className="w-[40%] bg-white border border-gray-200 rounded-lg p-6 overflow-y-auto">
          <h2 className="text-lg font-semibold text-gray-700 mb-4">
            Appointment Details
          </h2>

          <form className="space-y-4" onSubmit={handleLocalSubmit}>
            {/* Date and Status Row */}
            <div className="grid grid-cols-2 gap-4 pb-4 border-b">
              <div>
                <label className="block text-gray-700 text-sm mb-1">
                  Appointment Date
                </label>
                <AntDatePicker
                  placeholder="Select date"
                  value={appointmentDate}
                  onChange={(date) => setAppointmentDate(date)}
                  disabledFutureDate={false}
                />
              </div>
              <div>
                <label className="block text-gray-700 text-sm mb-1">
                  Status
                </label>
                <CustomSelect
                  value={appointmentStatus}
                  isSearchable={false}
                  isClearable={false}
                  className="w-full"
                  onChange={(e) => setAppointmentStatus(e.value)}
                  options={[
                    { value: 0, label: "Waiting" },
                    { value: 1, label: "In Progress" },
                    { value: 2, label: "Completed" },
                    { value: 3, label: "Canceled" },
                  ]}
                />
              </div>
            </div>

            {/* Patient Information */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label
                  htmlFor="mobile"
                  className="block text-gray-700 text-sm mb-1"
                >
                  Mobile Number
                </label>
                <PatientAutocomplete
                  patient={patient}
                  setPatient={setPatient}
                  patientDetails={patientDetails}
                  setPatientDetails={setPatientDetails}
                  setName={setName}
                  setGender={setGender}
                  setAge={setAge}
                  setBirthDate={setBirthDate}
                  isNewRelatedPatient={isNewRelatedPatient}
                  setIsNewRelatedPatient={setIsNewRelatedPatient}
                  checkRelatedPatient={checkRelatedPatient}
                  setCheckRelatedPatient={setCheckRelatedPatient}
                  resetForm={resetForm}
                />
                <div className="text-sm text-red-500">
                  {errorMessage?.patientContactNumber}
                </div>
              </div>

              <div className="col-span-2">
                <label
                  htmlFor="name"
                  className="block text-gray-700 text-sm mb-1"
                >
                  Patient Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={
                    patientDetails &&
                    patientDetails.patient_id &&
                    patientDetails.full_name &&
                    isNewRelatedPatient !== true
                  }
                  className="w-full px-3 py-2 border rounded-md text-gray-700"
                />
                <div className="text-sm text-red-500">
                  {errorMessage?.name}
                </div>
              </div>

              <div className="col-span-2">
                <label
                  htmlFor="gender"
                  className="block text-gray-700 text-sm mb-1"
                >
                  Gender
                </label>
                <div className="flex space-x-4">
                  <button
                    type="button"
                    disabled={
                      patientDetails &&
                      patientDetails.patient_id &&
                      patientDetails.gender &&
                      isNewRelatedPatient !== true
                    }
                    className={`px-4 py-2 rounded-md ${
                      gender === "Male"
                        ? "bg-blue-500 text-white"
                        : "bg-gray-200 text-gray-700"
                    }`}
                    onClick={() => setGender("Male")}
                  >
                    Male
                  </button>
                  <button
                    type="button"
                    disabled={
                      patientDetails &&
                      patientDetails.patient_id &&
                      patientDetails.gender &&
                      isNewRelatedPatient !== true
                    }
                    className={`px-4 py-2 rounded-md ${
                      gender === "Female"
                        ? "bg-blue-500 text-white"
                        : "bg-gray-200 text-gray-700"
                    }`}
                    onClick={() => setGender("Female")}
                  >
                    Female
                  </button>
                  <button
                    type="button"
                    disabled={
                      patientDetails &&
                      patientDetails.patient_id &&
                      patientDetails.gender &&
                      isNewRelatedPatient !== true
                    }
                    className={`px-4 py-2 rounded-md ${
                      gender === "Other"
                        ? "bg-blue-500 text-white"
                        : "bg-gray-200 text-gray-700"
                    }`}
                    onClick={() => setGender("Other")}
                  >
                    Other
                  </button>
                </div>
                <div className="text-sm text-red-500">
                  {errorMessage?.gender}
                </div>
              </div>

              <div>
                <label
                  htmlFor="age"
                  className="block text-gray-700 text-sm mb-1"
                >
                  Age
                </label>
                <input
                  type="text"
                  id="age"
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  onBlur={handleChangeAge}
                  disabled={
                    patientDetails &&
                    patientDetails.patient_id &&
                    patientDetails.age &&
                    isNewRelatedPatient !== true
                  }
                  className="w-full px-3 py-2 border rounded-md text-gray-700"
                />
              </div>

              <div>
                <label className="block text-gray-700 text-sm mb-1">
                  Date of birth
                </label>
                <AntDatePicker
                  defaultValue={null}
                  placeholder="select date"
                  value={date_of_birth ? dayjs(date_of_birth) : ""}
                  onChange={(dateString) => CalculateAgeFromDate(dateString)}
                  disabledFutureDate={true}
                  disabled={
                    patientDetails &&
                    patientDetails.patient_id &&
                    patientDetails.gender &&
                    isNewRelatedPatient !== true
                  }
                />
                {errorMessage?.date_of_birth != "" && (
                  <div className="text-red-500 text-sm">
                    {errorMessage?.date_of_birth}
                  </div>
                )}
              </div>

              <div className="col-span-2">
                <label
                  htmlFor="doctor"
                  className="block text-gray-700 text-sm mb-1"
                >
                  Doctor
                </label>
                <CustomSelect
                  id="doctor"
                  value={localSelectedDoctor}
                  isSearchable={true}
                  isClearable={false}
                  isDisabled={false}
                  className="w-full form_select"
                  placeholder="Select Doctor"
                  menuPosition="absolute"
                  onChange={(e) => {
                    setLocalSelectedDoctor(e.value);
                  }}
                  options={doctors.map((doctor) => ({
                    value: doctor.doctor_id,
                    label: `${doctor.first_name} ${doctor.last_name}`,
                  }))}
                />
                <div className="text-sm text-red-500">
                  {errorMessage?.localSelectedDoctor}
                </div>
              </div>
            </div>

            <button
              type="submit"
              className="w-full bg-blue-500 text-white py-2 rounded-md hover:bg-blue-600 flex justify-center"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <FaSpinner size={20} className="animate-spin" />
              ) : (
                "Add Appointment"
              )}
            </button>
          </form>
        </div>
      </div>

      {/* Billing Modal */}
      {showBillingForm && (
        <BillingForm
          patientId={selectedPatientId}
          appointmentDate={selectedAppointmentDate}
          addDefaultConsultationService={true}
          selectedAppointmentDoctor={selectedAppointmentDoctor}
          onClose={handleCloseBillingForm}
        />
      )}
    </div>
  );
};

export default RapidEntry;
